import { 
  HiUser, 
  HiEnvelope, 
  HiIdentification,
  HiBuildingOffice2,
  HiCalendar,
  HiCheckCircle,
  HiXCircle,
  HiPencil,
  HiTrash
} from 'react-icons/hi2'
import { UserAvatar } from '../../../components/UserAvatar'
import { Badge, Button } from '../../../components'
import { BaseModal } from '../../../components/Modal/BaseModal'
import type { User } from '../../../models'

interface UserDetailsModalProps {
  isOpen: boolean
  user: User | null
  onClose: () => void
  onEdit?: (user: User) => void
  onDelete?: (user: User) => void
}

export const UserDetailsModal = ({ isOpen, user, onClose, onEdit, onDelete }: UserDetailsModalProps) => {
  if (!user) return null

  const getFuncaoBadgeColor = (funcao: string) => {
    switch (funcao) {
      case 'ADMIN':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
      case 'DIRETOR':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
      case 'USUARIO':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Detalhes do Usuário"
      icon={<HiUser className="w-6 h-6" />}
      size="xl"
      useStandardLayout={true}
      resizable={true}
      stickyFooter={true}
      footerContent={
        <div className="flex items-center justify-between p-4">
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
          <div className="flex items-center gap-3">
            {onDelete && (
              <Button
                variant="destructive"
                onClick={() => {
                  onClose()
                  onDelete(user)
                }}
                className="flex items-center gap-2"
              >
                <HiTrash className="w-4 h-4" />
                Desativar
              </Button>
            )}
            {onEdit && (
              <Button
                variant="primary"
                onClick={() => {
                  onClose()
                  onEdit(user)
                }}
                className="flex items-center gap-2"
              >
                <HiPencil className="w-4 h-4" />
                Editar
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div className="p-4 space-y-6">
        {/* Custom Header with Avatar */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 -mx-4 -mt-4 px-6 py-6 rounded-t-lg">
          <div className="flex items-center gap-4">
            <UserAvatar
              user={user}
              size="lg"
              showName={false}
              showPopup={false}
              className="border-4 border-white/20"
            />
            <div>
              <h3 className="text-xl font-bold text-white">
                {user.name}
              </h3>
              <p className="text-blue-100 text-sm mt-1">{user.email}</p>
              <div className="mt-2">
                <Badge className={getFuncaoBadgeColor(user.funcao_usuario)}>
                  {user.funcao_usuario}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
                {/* Basic Information */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                    <HiUser className="w-5 h-5" />
                    Informações Básicas
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-1">
                        <HiEnvelope className="w-4 h-4" />
                        <span className="text-xs font-medium uppercase">Email</span>
                      </div>
                      <p className="text-gray-900 dark:text-gray-100 font-medium">{user.email}</p>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-1">
                        <HiIdentification className="w-4 h-4" />
                        <span className="text-xs font-medium uppercase">Cargo</span>
                      </div>
                      <p className="text-gray-900 dark:text-gray-100 font-medium">
                        {user.cargo_usuario || 'Não especificado'}
                      </p>
                    </div>

                    {user.setor_logado && (
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-1">
                          <HiBuildingOffice2 className="w-4 h-4" />
                          <span className="text-xs font-medium uppercase">Setor Atual</span>
                        </div>
                        <p className="text-gray-900 dark:text-gray-100 font-medium">
                          {user.setor_logado.nome_setor}
                        </p>
                      </div>
                    )}

                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-1">
                        <HiCheckCircle className="w-4 h-4" />
                        <span className="text-xs font-medium uppercase">Notificações por Email</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {user.emails ? (
                          <HiCheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <HiXCircle className="w-5 h-5 text-red-600" />
                        )}
                        <p className="text-gray-900 dark:text-gray-100 font-medium">
                          {user.emails ? 'Ativado' : 'Desativado'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sectors */}
                {user.setores && user.setores.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                      <HiBuildingOffice2 className="w-5 h-5" />
                      Setores Vinculados ({user.setores.length})
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {user.setores.map(setor => (
                        <div
                          key={setor.id}
                          className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-900/50 rounded-lg p-4"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h5 className="font-medium text-blue-900 dark:text-blue-100">
                                {setor.nome_setor}
                              </h5>
                              {setor.pivot && (
                                <div className="mt-2 flex items-center gap-2">
                                  <Badge className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
                                    {setor.pivot.funcao || 'USUARIO'}
                                  </Badge>
                                  {setor.pivot.active && (
                                    <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                                      <HiCheckCircle className="w-3 h-3" />
                                      Ativo
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Timestamps */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                    <HiCalendar className="w-5 h-5" />
                    Informações do Sistema
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {user.created_at && (
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                        <div className="text-xs font-medium uppercase text-gray-600 dark:text-gray-400 mb-1">
                          Criado em
                        </div>
                        <p className="text-gray-900 dark:text-gray-100 font-medium">
                          {formatDate(user.created_at)}
                        </p>
                      </div>
                    )}

                    {user.updated_at && (
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                        <div className="text-xs font-medium uppercase text-gray-600 dark:text-gray-400 mb-1">
                          Última atualização
                        </div>
                        <p className="text-gray-900 dark:text-gray-100 font-medium">
                          {formatDate(user.updated_at)}
                        </p>
                      </div>
                    )}

                    {user.email_verified_at && (
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                        <div className="text-xs font-medium uppercase text-gray-600 dark:text-gray-400 mb-1">
                          Email verificado em
                        </div>
                        <p className="text-gray-900 dark:text-gray-100 font-medium">
                          {formatDate(user.email_verified_at)}
                        </p>
                      </div>
                    )}

                    {user.azure_id && (
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                        <div className="text-xs font-medium uppercase text-gray-600 dark:text-gray-400 mb-1">
                          Azure ID
                        </div>
                        <p className="text-gray-900 dark:text-gray-100 font-medium font-mono text-xs">
                          {user.azure_id}
                        </p>
                      </div>
            )}
          </div>
        </div>
      </div>
    </BaseModal>
  )
}

